import { useTranslation } from "react-i18next";

interface FeatureContent {
	title?: string | string[];
	showTitleIcon?: boolean;
	features?: {
		title?: string;
		items?: string[];
	}[];
}

interface FeatureContentMap {
	[key: string]: {
		[key: string]: FeatureContent;
	};
}

export const usePricingFeatureContent = () => {
	const { t } = useTranslation("translation", { keyPrefix: "pricingFlow.pricingPopup" });

	const featureContent: FeatureContentMap = {
		freeTrial: {
			aiGenerateEdit: {
				title: [t("generalPurpose"), t("industrySpecificCapabilities")],
				features: [
					{
						title: t("generalPurpose"),
						items: [t("imageGeneration"), t("imageEditing"), t("videoEditing")],
					},
					{
						title: t("industrySpecificCapabilities"),
						items: [t("retail"), t("automotive")],
					},
				],
			},
			commercialLicenseForModelsWeights: {
				title: "-",
			},
			accessMethods: {
				title: t("apiMcpIframeComfyui"),
			},
			tailoredGenerationManagedFineTuning: {
				title: t("engines"),
				features:[
					{
						items: [t("trainUpTo3EnginesViaBriaConsole")],
					}
				]
			},
			throughput: {
				title: t("10actionsPerMinute"),
			},
			hosting: {
				title: t("hostedByBria"),
			},
			latencyHandling: {
				title: t("standardQueue"),
			},
			support: {
				title: t("development"),
			},
			developersExperienceSuite: {
				title: t("standardDevTools"),
				features:[
					{
						items: [t("fullDocumentation"), t("learningHub"), t("demoPlayground"), t("howToRecipes")],
					}
				]
			},
			riskFreeGenAi: {
				title: t("limited"),
				features: [
					{
						items: [t("trainedOn100LicensedData"), t("indemnityCapped"), t("builtinC2PA"), t("builtinContentModeration")],
					},
				],
			},
			security: {
				title: t("sso"),
			},
		},
		selfService: {
			aiGenerateEdit: {
				title: t("generalPurpose"),
				features: [
					{
						items: [t("imageGeneration"), t("imageEditing"), t("videoEditing")],
					},
				],
			},
			commercialLicenseForModelsWeights: {
				title: "-",
			},
			accessMethods: {
				title: t("chosenAccessMethod"),
			},
			tailoredGenerationManagedFineTuning: {
				title: "-",
			},
			throughput: {
				title: t("60actionsPerMinute"),
			},
			hosting: {
				title: t("hostedByBria"),
			},
			latencyHandling: {
				title: t("prioritizedQueue"),
			},
			support: {
				title: t("production"),
			},
			developersExperienceSuite: {
				title: t("standardDevTools"),
				features:[
					{
						items: [t("fullDocumentation"), t("learningHub"), t("demoPlayground"), t("howToRecipes")],
					}
				]
			},
			riskFreeGenAi: {
				title: t("limited"),
				features: [
					{
						items: [t("trainedOn100LicensedData"), t("indemnityCapped"), t("builtinC2PA"), t("builtinConfigurableContentModeration")],
					},
				],
			},
			security: {
				title: t("sso"),
			},
		},
		briaService: {
			aiGenerateEdit: {
				title: t("all"),
				showTitleIcon: false,
				features: [
					{
						title: t("generalPurpose"),
						items: [t("imageGeneration"), t("imageEditing"), t("videoEditing")],
					},
					{
						title: t("industrySpecificCapabilities"),
						items: [t("retail"), t("automotive"), t("marketing")],
					},
				],
			},
			commercialLicenseForModelsWeights: {
				title: t("included"),
			},
			accessMethods: {
				title: t("all"),
				features: [
					{
						items: [t("api"), t("mcp"), t("iframe"), t("photoshopPlugin"), t("figmaPlugin"), t("comfyUI")],
					},
				],
			},
			tailoredGenerationManagedFineTuning: {
				title: t("fullSuite"),
				features: [
					{
						items: [t("unlimitedEngines"), t("iFrameAndApiIntegration"), t("reinforcementLearning")],
					},
				],
			},
			throughput: {
				title: t("custom"),
			},
			hosting: {
				title: t("flexibleDeployment"),
				features: [
					{
						items: [t("hostedByBria"), t("byoc"), t("onPrem")],
					},
				],
			},
			latencyHandling: {
				title: t("dedicatedLowLatencyQueue"),
			},
			support: {
				title: t("enterprise"),
			},
			developersExperienceSuite: {
				title: t("premiumDevEnablement"),
				features: [
					{
						items: [t("fullDocumentation"), t("learningHub"), t("demoPlayground"), t("howToRecipes"), t("errorLoggingAndTracking")],
					},
				],
			},
			riskFreeGenAi: {
				title: t("full"),
				features: [
					{
						items: [t("trainedOn100LicensedData"), t("fullIndemnity"), t("builtinConfigurableC2PA"), t("copyrightabilityReportPerProject"), t("builtinConfigurableContentModeration")],
					},
				],
			},
			security: {
				title: t("ssoMfa"),
			},
		},
	};

	const getFeatureContent = (planKey: string, featureId: string): FeatureContent => {
		return featureContent[planKey]?.[featureId] || { title: "—", items: [] };
	};

	return {
		featureContent,
		getFeatureContent,
	};
};

export type { FeatureContent, FeatureContentMap }; 